import {StringUtils, Logger, ArgValidator, NumberUtils, Utils, StringsFactory, lodash} from 'neo-common';
import {GameConfigurationBase} from './GameConfigurationBase';
import {GameDefinitionBase} from './GameDefinitionBase';
import {IClientHost} from '../ClientHost/IClientHost';
import {ClientHostVolumeData, ClientHostMetadataTypes, ClientHostDialogRequest, ClientHostDialogType, OnOffState} from '../ClientHost/Types';
import {IClientHostConsumer} from '../ClientHost/IClientHostConsumer';

declare var require: any;

export abstract class AppContextBase<TGameConfiguration extends GameConfigurationBase,
    TGameDefinition extends GameDefinitionBase> {

    private static _context: AppContextBase<any, any> = null;

    private _currency: string;
    private _gameConfiguration: TGameConfiguration;
    private _gameDefinition: TGameDefinition;
    private _clientHost: IClientHost;
    private _gameKey: string;
    private _clientHostConsumer: IClientHostConsumer;

    constructor(gameConfiguration: TGameConfiguration, gameDefinition: TGameDefinition, gameKey: string) {
        this._gameConfiguration = gameConfiguration;
        this._gameDefinition = gameDefinition;
        this._gameKey = gameKey;

        let query = Utils.getURIQuery();
        this._currency = query['currency'];

        AppContextBase._context = this;
    }

    public static getInstance(): AppContextBase<any, any> {
        return AppContextBase._context;
    }

    /**
     * gets game key
     */
    public get gameKey(): string {
        return this._gameKey;
    }

    /**
     * Gets curreny code (from current url query string 'currency')
     * @returns {string} - currency code
     */
    public get currency(): string {
        return this._currency;
    }

    /**
     * Gets game configuration
     * @returns {TGameConfiguration}
     */
    public get gameConfiguration(): TGameConfiguration {
        return this._gameConfiguration;
    }

    /**
     * Gets game definition
     * @returns {TGameDefinition}
     */
    public get gameDefinition(): TGameDefinition {
        return this._gameDefinition;
    }

    /**
     * Gets client host interface for application.
     * @returns {IClientHost} instance of class implementing @IClientHost interface.
     */
    public get clientHost(): IClientHost {
        return this._clientHost;
    }

    /**
     * Gets client host consumer implementation for application
     * @returns {IClientHostConsumer} instance of class implementing @IClientHostConsumer interface.
     */
    public get clientHostConsumer(): IClientHostConsumer {
        return this._clientHostConsumer;
    }

    /**
     * Determine whenever force tool is enabled or not.
     * @returns {boolean} true if force bool is enabled in game configuration and is also active by query string
     */
    public get forceToolEnabled(): boolean {
        return this.gameConfiguration.ForceToolEnabled;
    }

    /**
     * Format number according to game configuration @GameConfigurationBase.NumberFormat property.
     * @param value - number value to be formatted.
     * @returns {string} Formatted number as string.
     */
    public formatNumber(value: number): string {
        return NumberUtils.formatNumber(value, this.gameConfiguration.NumberFormat);
    }

    /**
     * Gets current volume settings for obtained from client host (only if client host supports volume)
     * @returns non-null @ClientHostVolumeData when client host was successfully loaded and supports volume feature, otherwise returns @null.
     */
    public getVolume(): ClientHostVolumeData {
        let result: ClientHostVolumeData = null;
        if (this._clientHost) {
            if (DEBUG) {
                if (!this._clientHost.supports.volume) {
                    Logger.debugGroup('AppContext',
                        `Could not get volume because client host '${this._gameDefinition.ClientHostType}'` +
                        ` does not support this feature!`);
                }
            }

            result = this._clientHost.supports.volume ? this._clientHost.getVolume() : null;
        }

        return result;
    }

    /**
     * Gets flag whenever background music is enabled or not.
     * @returns true if enabled or false if not.
     */
    public get isBackgroundMusicEnabled(): boolean {
        if (this._clientHost) {
            return this._clientHost.getMetadata<boolean>(ClientHostMetadataTypes.BackgroundMusic);
        }

        return true;
    }

    /**
     * Gets flag whenever user can mute volume or not.
     * @returns true if allowed or false if not.
     */
    public get isMuteVolumeAllowed(): boolean {
        if (this._clientHost) {
            return this._clientHost.getMetadata<boolean>(ClientHostMetadataTypes.MuteVolumeAllowed);
        }

        return true;
    }

    /**
     * Sets current denomination value to client host.
     * @param {number} denomination
     */
    public setDenomination(denomination: number): void {
        if (this._clientHost) {
            let supportsDenomination = this._clientHost.supports.denomination;

            if (DEBUG) {
                if (!supportsDenomination) {
                    Logger.debugGroup('AppContext',
                        `Could not set denomination because client host '${this._gameDefinition.ClientHostType}'` +
                        ` does not support this feature!`);

                    return;
                }
            }

            if (supportsDenomination) {
                if (DEBUG) {
                    Logger.debugGroup('AppContext',
                        `'${this._gameDefinition.ClientHostType}' trying to set denomination to '${denomination}'..`);
                }

                this._clientHost.setDenomination(denomination);

                if (DEBUG) {
                    Logger.debugGroup('AppContext',
                        `'${this._gameDefinition.ClientHostType}' successfully sets denomination to '${denomination}'.`);
                }
            }
        }
    }

    /**
     * Gets array of allowed languages.
     * @returns {Array<string>}
     */
    public getAllowedLanguages(): Array<string> {
        // this is list of available language codes which are defined in file 'Strings-metadata.json' provided by StringsFactory
        const availableLanguages = StringsFactory.getLanguages();
        let result: Array<string> = [];

        if (this._clientHost) {
            const clientHostLanguages = this._clientHost.getAllowedLanguages();
            if (clientHostLanguages && clientHostLanguages.length > 0) {
                result = lodash.filter(clientHostLanguages, lang => availableLanguages.indexOf(lang) > -1);
            }

            if (result.length === 0) {
                let language = lodash.find(availableLanguages, lang => lang === 'en');
                if (language === null && availableLanguages.length > 0) {
                    language = availableLanguages[0];
                }

                result.push(language);
            }
        }

        return result;
    }

    /**
     * Gets language code of currently loaded string resources.
     * @returns {string}
     */
    public getCurrentLanguage(): string {
        return StringsFactory.currentLanguage;
    }

    /**
     * Gets current denomination value from client host.
     * @returns {number}
     */
    public getDenomination(): number {
        if (this._clientHost) {
            let supportsDenomination = this._clientHost.supports.denomination;

            if (DEBUG) {
                if (!supportsDenomination) {
                    Logger.debugGroup('AppContext',
                        `Could not get denomination because client host '${this._gameDefinition.ClientHostType}'` +
                        ` does not support this feature!`);

                    return;
                }
            }

            if (supportsDenomination) {
                return this._clientHost.getDenomination();
            }
        }

        return null;
    }

    /**
     * Sets current volume, only usefull for client hosts which supports 'volume' feature.
     * @param stepNo - volume setting represented by step number from range 0-3.
     */
    public setVolume(stepNo: number): void {
        if (this._clientHost) {
            let supportsVolume = this._clientHost.supports.volume;

            if (DEBUG) {
                if (!supportsVolume) {
                    Logger.debugGroup('AppContext',
                        `Could not set volume because client host '${this._gameDefinition.ClientHostType}'` +
                        ` does not support this feature!`);

                    return;
                }
            }

            if (supportsVolume) {
                if (stepNo >= 0 && stepNo <= 3) {
                    let currentVolume = this.getVolume();

                    if (!Utils.isNullOrUndefined(currentVolume)) {
                        this._clientHost.setVolume(stepNo);

                        if (DEBUG) {
                            Logger.debugGroup('AppContext',
                                `'${this._gameDefinition.ClientHostType}' volume was set by step ${stepNo}`);
                        }
                    } else {
                        if (DEBUG) {
                            Logger.errorGroup('AppContext',
                                `Could not set volume by step '${stepNo}', could not get current volume settings!`);
                        }
                    }
                } else {
                    if (DEBUG) {
                        Logger.errorGroup('AppContext',
                            `Could not set volume by step '${stepNo}' which is outside of allowed range 0-3!`);
                    }
                }
            }
        }
    }

    /**
     * request to show dialog with message (optionally error)
     * @param request - show dialog request data
     */
    public showDialog(text: string, isError: boolean, code: string = '',
        errorId: string = '', gameRoundId: string = '', customData: Object = null): void {

        if (this._clientHost) {
            let request: ClientHostDialogRequest = {
                text: text,
                type: isError ? ClientHostDialogType.Error : ClientHostDialogType.Normal,
                error: null,
                data: customData
            };

            if (isError) {
                if (StringUtils.isNullOrEmpty(errorId)) {
                    errorId = '0';
                }

                let fullErrorCode = `${this._gameKey}-${code}-${gameRoundId}-${errorId}`;

                request.error = {
                    //errorCode: fullErrorCode,
                    errorCode: code,
                    errorMessageTitle: text,
                    errorMessageDescription: fullErrorCode
                };
            }

            if (DEBUG) {
                Logger.debugGroup('AppContext', `'${this._gameDefinition.ClientHostType}'.showDialog(), request: `, request);
            }

            this._clientHost.showDialog(request);
        }
    }

    /**
     * request to close dialog (if is visible now)
     */
    public closeDialog(): void {
        if (this._clientHost) {
            if (DEBUG) {
                Logger.debugGroup('AppContext', `'${this._gameDefinition.ClientHostType}'.closeDialog()`);
            }

            this._clientHost.closeDialog();
        }
    }

    /**
     * Sets current language.
     * @param {string} language code
     */
    public setLanguage(language: string): void {
        StringsFactory.change(language);

        if (this._clientHost) {
            if (DEBUG) {
                Logger.debugGroup('AppContext', `'${this._gameDefinition.ClientHostType}'.setLanguage('${language}')`);
            }

            this._clientHost.setLanguage(language);
        }
    }

    /**
     * Gets background music state: true - on, false - off.
     * @returns {boolean}
     */
    public getBackgroundMusic(): boolean {
        if (this._clientHost) {
            const supportsBackgroundMusic = this.isBackgroundMusicEnabled;

            if (DEBUG) {
                if (!supportsBackgroundMusic) {
                    Logger.debugGroup('AppContext',
                        `Could not get background music because client host '${this._gameDefinition.ClientHostType}' ` +
                        `does not support background music!`);
                    return;
                }
            }
            if (supportsBackgroundMusic) {
                return this._clientHost.getBackgroundMusic() === OnOffState.On;
            }
        }
        return true;
    }

    /**
     * Sets current background music - true(on) or false(off).
     */
    public setBackgroundMusic(on: boolean): void {
        if (this._clientHost) {
            const supportsBackgroundMusic = this.isBackgroundMusicEnabled;

            if (DEBUG) {
                if (!supportsBackgroundMusic) {
                    Logger.debugGroup('AppContext',
                        `Could not set background music because client host '${this._gameDefinition.ClientHostType}' ` +
                        `does not support background music!`);
                    return;
                }
            }

            if (supportsBackgroundMusic) {
                const value = on ? OnOffState.On : OnOffState.Off;
                if (DEBUG) {
                    Logger.debugGroup('AppContext', `'${this._gameDefinition.ClientHostType}' trying to set background music to '${value}'..`);
                }

                this._clientHost.setBackgroundMusic(value);

                if (DEBUG) {
                    Logger.debugGroup('AppContext', `'${this._gameDefinition.ClientHostType}' successfully sets background music to '${value}'.`);
                }
            }
        }
    }

    /**
     * Gets shared setting specified by key.
     */
    public getSharedSetting(key: string): string {
        if (this._clientHost) {
            const supportsSettings = this._clientHost.supports.settings;

            if (DEBUG) {
                if (!supportsSettings) {
                    Logger.debugGroup('AppContext',
                        `Could not get shared setting because client host '${this._gameDefinition.ClientHostType}' ` +
                        `does not support this feature!`);
                    return;
                }
            }
            if (supportsSettings) {
                return this._clientHost.getSharedSetting(key);
            }
        }

        return undefined;
    }

    /**
     * Gets game setting specified by key.
     */
    public getGameSetting(key: string): string {
        if (this._clientHost) {
            const supportsSettings = this._clientHost.supports.settings;

            if (DEBUG) {
                if (!supportsSettings) {
                    Logger.debugGroup('AppContext',
                        `Could not get shared setting because client host '${this._gameDefinition.ClientHostType}' ` +
                        `does not support this feature!`);
                    return;
                }
            }

            if (supportsSettings) {
                return this._clientHost.getGameSetting(key);
            }
        }

        return undefined;
    }

    /**
     * Updates shared setting specified by key.
     */
    public updateSharedSetting(key: string, value: string): void {
        if (this._clientHost) {
            const supportsSettings = this._clientHost.supports.settings;

            if (DEBUG) {
                if (!supportsSettings) {
                    Logger.debugGroup('AppContext',
                        `Could not set shared setting because client host '${this._gameDefinition.ClientHostType}' ` +
                        `does not support this feature!`);
                    return;
                }
            }

            if (supportsSettings) {
                this._clientHost.updateSharedSetting(key, value);
            }
        }
    }

    /**
     * Updates game setting specified by key.
     */
    public updateGameSetting(key: string, value: string): void {
        if (this._clientHost) {
            const supportsSettings = this._clientHost.supports.settings;

            if (DEBUG) {
                if (!supportsSettings) {
                    Logger.debugGroup('AppContext',
                        `Could not set game setting because client host '${this._gameDefinition.ClientHostType}' ` +
                        `does not support this feature!`);
                    return;
                }
            }
            if (supportsSettings) {
                this._clientHost.updateGameSetting(key, value);
            }
        }
    }

    public sendMessageToExternal(message: Object, targetExternalId: string[]): void {
        if (this._clientHost) {
            let supportsExternalMessage = this._clientHost.supports.externalMessage;

            if (DEBUG) {
                if (!supportsExternalMessage) {
                    Logger.debugGroup('AppContext',
                        `Could not send external message because client host '${this._gameDefinition.ClientHostType}'` +
                        ` does not support this feature!`);

                    return;
                }
            }

            if (supportsExternalMessage) {
                if (DEBUG) {
                    Logger.debugGroup('AppContext',
                        `'${this._gameDefinition.ClientHostType}' sending external message ...`);
                }

                this._clientHost.sendMessageToExternal(message, targetExternalId);
            }
        }
    }

}

export function updateClientHost(appContext: AppContextBase<any, any>, clientHost: IClientHost,
    clientHostConsumer: IClientHostConsumer): void {

    ArgValidator.ensureIsNotNullOrUndefined(appContext, 'appContext');
    ArgValidator.ensureIsNotNullOrUndefined(clientHost, 'clientHost');
    ArgValidator.ensureIsNotNullOrUndefined(clientHostConsumer, 'clientHostConsumer');

    (appContext as any)._clientHost = clientHost;
    (appContext as any)._clientHostConsumer = clientHostConsumer;
}


/** WEBPACK FOOTER **
 ** ./Engine/GameEngine/AppContextBase.ts
 **/